import { useEffect, useRef, useState, type FormEvent } from "react";
import { captureEvent } from "@/src/lib/posthog";

import { useNavigate, useOutletContext, useSearchParams } from "react-router";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import { Icon } from "@/components/icons";
import { Tooltip } from "@/components/tooltip";
import { IntroHeader } from "@/components/intro-header";
import {
  ConfidenceCard,
  CountryCard,
  DataSourceCard,
  DobCard,
  ENTITY_TYPES,
  EntityTypeCard,
  type EntityType,
} from "@/components/filter-cards";
import { useScreeningConfigs } from "@/app/(app)/settings/_screening-configs-store";
import { validateDob, validateIdentifier } from "../_schemas/screening-search";
import { Alert } from "@/components/alert";
import { useToast } from "@/components/action-feedback";
import {
  hasAdverseMediaSettings,
  listSearchConfigurations,
  saveUserSanctionsDefault,
  type AdverseMediaConfig,
} from "@/src/services/search_configurations_service";
import {
  apiTokenForSelection,
  selectionIdForApiToken,
  useDataSources,
} from "@/components/watchlists";
import {
  AdverseMediaEntityTypeHint,
  AdverseMediaNameRequiredHint,
  AdverseMediaSettingsHint,
} from "@/app/(app)/_components/adverse-media-info";
import {
  ADVERSE_MEDIA_COLLECTION_ID,
  ADVERSE_MEDIA_RESTRICTED_ENTITY_TYPES,
  ADVERSE_MEDIA_UNUSED_FIELD_TOOLTIP,
  buildAdverseMediaTooltip,
} from "@/app/(app)/_components/adverse-media-info-utils";

function setParam(sp: URLSearchParams, key: string, value: string) {
  if (value) sp.set(key, value);
  else sp.delete(key);
}

const EMPTY_FILTERS = {
  confidenceScore: 88,
  entityType: ["Individual", "Organization"] as EntityType[],
  dob: "",
  watchlists: ["coll:sanctions-lists"] as string[],
  countries: [] as string[],
};

/** The user's saved sanctions config, once the async fetch in `SearchSurface`
 *  resolves — remembered so a later same-route resync (e.g. clicking the
 *  breadcrumb back to a bare `/screening-portal`) can fall back to it instead
 *  of the generic `EMPTY_FILTERS` placeholder. */
type SavedFilterDefaults = {
  confidence?: number;
  entityType?: EntityType[];
  dataSources?: string[];
  countries?: string[];
};

function filtersFromParams(
  params: URLSearchParams,
  defaultDob?: string,
  savedDefaults?: SavedFilterDefaults,
) {
  const c = Number(params.get("conf"));
  const confidence =
    Number.isFinite(c) && c >= 80 && c <= 99
      ? c
      : (savedDefaults?.confidence ?? EMPTY_FILTERS.confidenceScore);

  const t = params.get("type");
  const types = t
    ? (t.split(",").filter((v) => (ENTITY_TYPES as readonly string[]).includes(v)) as EntityType[])
    : [];
  const entityType = types.length ? types : (savedDefaults?.entityType ?? EMPTY_FILTERS.entityType);

  const dob = params.get("dob") ?? defaultDob ?? EMPTY_FILTERS.dob;

  const wl = params.get("wl");
  const wlIds = wl ? wl.split(",").filter(Boolean) : [];
  const dataSources = wlIds.length
    ? wlIds
    : (savedDefaults?.dataSources ?? EMPTY_FILTERS.watchlists);

  const country = params.get("country");
  const countries = country
    ? country.split(",").filter(Boolean)
    : (savedDefaults?.countries ?? EMPTY_FILTERS.countries);

  return { confidence, entityType, dob, dataSources, countries };
}

const API_ENTITY_TYPE_TO_UI: Record<string, string> = {
  Entity: "Organization",
  Individual: "Individual",
  Vessel: "Vessel",
  Aircraft: "Aircraft",
  Country: "Country",
};

export function Header() {
  return (
    <IntroHeader
      iconName="search"
      title="Screen a name"
      description="Search for an individual, organization, vessel, or aircraft across global sanctions lists, PEP databases, criminal watchlists, and adverse media."
    />
  );
}

type SearchSurfaceProps = {
  initialQuery?: string;
  isResults?: boolean;
};

export function SearchSurface({ initialQuery = "", isResults = false }: SearchSurfaceProps) {
  const navigate = useNavigate();
  const ctx = useOutletContext<AppOutletContext | null>();
  const adverseMediaFeatureEnabled = !ctx?.infoResolved || (ctx?.adverseMediaEnabled ?? true);
  const pepFeatureEnabled = !ctx?.infoResolved || (ctx?.pepAllowed ?? true);
  const [params] = useSearchParams();
  const { items: dataSourceItems } = useDataSources();
  const configs = useScreeningConfigs();
  const defaultConfig = configs[0];
  const [draft, setDraft] = useState(initialQuery);
  const [identifier, setIdentifier] = useState(() => params.get("identifier") ?? "");
  const [confidence, setConfidence] = useState(
    () => filtersFromParams(params, defaultConfig?.dob).confidence,
  );
  const [entityType, setEntityType] = useState<EntityType[]>(
    () => filtersFromParams(params, defaultConfig?.dob).entityType,
  );
  const [dob, setDob] = useState(() => filtersFromParams(params, defaultConfig?.dob).dob);
  const [showDobError, setShowDobError] = useState(false);
  const [showIdentifierError, setShowIdentifierError] = useState(false);
  const [showAdverseMediaNameError, setShowAdverseMediaNameError] = useState(false);
  const [dataSources, setDataSources] = useState<string[]>(
    () => filtersFromParams(params, defaultConfig?.dob).dataSources,
  );
  const [countries, setCountries] = useState<string[]>(
    () => filtersFromParams(params, defaultConfig?.dob).countries,
  );
  const runAt = params.get("runAt");
  const historical = Boolean(runAt);
  const [companyAdverseMedia, setCompanyAdverseMedia] = useState<AdverseMediaConfig | null>(null);
  const [companyConfigLoaded, setCompanyConfigLoaded] = useState(false);
  const hasCompanyAdverseMediaSettings = !!companyAdverseMedia;
  const [userSanctionsConfigId, setUserSanctionsConfigId] = useState<string | undefined>();
  const [showFiltersOnNarrow, setShowFiltersOnNarrow] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const [saveDefaultError, setSaveDefaultError] = useState<string | null>(null);
  const [restoringDefault, setRestoringDefault] = useState(false);
  const [restoreDefaultError, setRestoreDefaultError] = useState<string | null>(null);
  const showToast = useToast();
  const userTouchedRef = useRef(false);
  const savedFilterDefaultsRef = useRef<SavedFilterDefaults>({});

  useEffect(() => {
    let cancelled = false;
    listSearchConfigurations().then((res) => {
      if (cancelled || !res.ok) return;

      // (1) Hydrate savable filters — never override a config the user started.
      if (!userTouchedRef.current) {
        const cfg =
          res.value.find((c) => c.type === "user" && c.sanctions) ??
          res.value.find((c) => c.sanctions);
        if (cfg?.id) setUserSanctionsConfigId(cfg.id);
        const sx = cfg?.sanctions;
        if (sx) {
          // Remembered regardless of the current `params.get(...)` gates below —
          // a LATER same-route resync (e.g. the breadcrumb back to a bare
          // `/screening-portal`) needs this saved default even if the search
          // that's active right now came in with its own URL filters.
          if (typeof sx.minScore === "number") {
            const c = Math.round(sx.minScore * 100);
            if (c >= 80 && c <= 99) {
              savedFilterDefaultsRef.current.confidence = c;
              if (!params.get("conf"))
                setConfidence((cur) => (cur === EMPTY_FILTERS.confidenceScore ? c : cur));
            }
          }
          if (sx.entityType) {
            const ui = API_ENTITY_TYPE_TO_UI[sx.entityType] ?? sx.entityType;
            if ((ENTITY_TYPES as readonly string[]).includes(ui)) {
              savedFilterDefaultsRef.current.entityType = [ui as EntityType];
              if (!params.get("type"))
                setEntityType((cur) =>
                  cur.join(",") === EMPTY_FILTERS.entityType.join(",") ? [ui as EntityType] : cur,
                );
            }
          }
          if (sx.dataSource.length) {
            const ids = sx.dataSource
              .map(selectionIdForApiToken)
              .filter((v): v is string => Boolean(v));
            if (ids.length) {
              savedFilterDefaultsRef.current.dataSources = ids;
              if (!params.get("wl"))
                setDataSources((cur) => {
                  const isDefaultPreselection =
                    cur.join(",") === EMPTY_FILTERS.watchlists.join(",");
                  return cur.length === 0 || isDefaultPreselection ? ids : cur;
                });
            }
          }
          if (sx.country.length) {
            savedFilterDefaultsRef.current.countries = sx.country;
            if (!params.get("country"))
              setCountries((cur) => (cur.length === 0 ? sx.country : cur));
          }
        }
      }

      // (2) Detect company-level Adverse Media defaults.
      const companyCfg = res.value.find((c) => c.type === "company" && c.adverseMedia);
      const am = companyCfg?.adverseMedia ?? null;
      setCompanyAdverseMedia(hasAdverseMediaSettings(am) ? am : null);
      setCompanyConfigLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSaveDefault() {
    if (savingDefault) return;
    setSavingDefault(true);
    setSaveDefaultError(null);
    const res = await saveUserSanctionsDefault(
      {
        countries,
        dataSources: dataSources.map(apiTokenForSelection).filter(Boolean),
        confidenceScore: confidence,
        entityType: entityType.length === 1 ? entityType[0] : null,
      },
      userSanctionsConfigId,
    );
    setSavingDefault(false);
    if (!res.ok) {
      setSaveDefaultError(res.error.message);
      return;
    }
    setUserSanctionsConfigId(res.value.id);
    showToast({ tone: "success", text: "Your search config have been saved" });
  }

  async function handleRestoreDefault() {
    if (!userSanctionsConfigId || restoringDefault) return;
    setRestoringDefault(true);
    setRestoreDefaultError(null);
    const res = await saveUserSanctionsDefault(
      {
        countries: EMPTY_FILTERS.countries,
        dataSources: [],
        confidenceScore: EMPTY_FILTERS.confidenceScore,
        entityType: null,
      },
      userSanctionsConfigId,
    );
    setRestoringDefault(false);
    if (!res.ok) {
      setRestoreDefaultError(res.error.message);
      return;
    }
    handleClear();
    showToast({ tone: "success", text: "Your search config have been restored" });
  }

  useEffect(() => {
    setDraft(params.get("q") ?? "");
    setIdentifier(params.get("identifier") ?? "");
    const isBare = !params.get("q") && !params.get("identifier");
    const filters = filtersFromParams(
      params,
      defaultConfig?.dob,
      isBare ? savedFilterDefaultsRef.current : undefined,
    );
    setConfidence(filters.confidence);
    setEntityType(filters.entityType);
    setDob(filters.dob);
    setDataSources(filters.dataSources);
    setCountries(filters.countries);
  }, [
    params.get("q"),
    params.get("identifier"),
    params.get("conf"),
    params.get("type"),
    params.get("dob"),
    params.get("wl"),
    params.get("country"),
    defaultConfig?.dob,
  ]);

  function handleClear() {
    userTouchedRef.current = true;
    setConfidence(EMPTY_FILTERS.confidenceScore);
    setEntityType(EMPTY_FILTERS.entityType);
    setDob(EMPTY_FILTERS.dob);
    setShowDobError(false);
    setDataSources(EMPTY_FILTERS.watchlists);
    setCountries(EMPTY_FILTERS.countries);
  }

  useEffect(() => {
    const onSignal = () => {
      handleClear();
      showToast({ tone: "info", text: "All search parameters have been reset" });
    };
    window.addEventListener("screening:clear-filters", onSignal);
    return () => window.removeEventListener("screening:clear-filters", onSignal);
  }, [showToast]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedName = draft.trim();
    const trimmedId = identifier.trim();
    if (!trimmedName && !trimmedId) return;

    if (dob && validateDob(dob)) {
      setShowDobError(true);
      return;
    }

    if (validateIdentifier(trimmedId)) {
      setShowIdentifierError(true);
      return;
    }

    if (adverseMediaSelected && !trimmedName) {
      setShowAdverseMediaNameError(true);
      return;
    }

    const entityTypeParam = entityType.join(",");
    const dataSourcesParam = dataSources.join(",");
    const countriesParam = countries.join(",");

    const next = new URLSearchParams(params.toString());
    setParam(next, "q", trimmedName);
    setParam(next, "identifier", trimmedId);
    next.delete("runAt");
    setParam(next, "conf", confidence !== EMPTY_FILTERS.confidenceScore ? String(confidence) : "");
    setParam(next, "type", entityTypeParam);
    setParam(next, "dob", dob);
    setParam(next, "wl", dataSourcesParam);
    setParam(next, "country", countriesParam);
    navigate(`?${next.toString()}`);
    captureEvent("Search Single Screening", {
      has_name: Boolean(trimmedName),
      has_identifier: Boolean(trimmedId),
      entity_type: entityTypeParam || null,
      confidence,
      has_dob: Boolean(dob),
      data_sources: dataSourcesParam,
      countries: countriesParam,
    });
  }

  const trimmedDraft = draft.trim();
  const trimmedIdentifier = identifier.trim();
  const hasQuery = trimmedDraft.length > 0 || trimmedIdentifier.length > 0;

  const matchesActiveSearch =
    hasQuery &&
    !historical &&
    trimmedDraft === (params.get("q") ?? "") &&
    trimmedIdentifier === (params.get("identifier") ?? "") &&
    (confidence !== EMPTY_FILTERS.confidenceScore ? String(confidence) : "") ===
      (params.get("conf") ?? "") &&
    entityType.join(",") === (params.get("type") ?? "") &&
    dob === (params.get("dob") ?? "") &&
    dataSources.join(",") === (params.get("wl") ?? "") &&
    countries.join(",") === (params.get("country") ?? "");

  const adverseMediaSelected = dataSources.includes(ADVERSE_MEDIA_COLLECTION_ID);
  // Identifier/DOB are only truly dead weight once Adverse Media is the ONLY
  // watchlist — mixed with others, they still drive the other lists' search.
  const adverseMediaOnly =
    dataSources.length > 0 && dataSources.every((id) => id === ADVERSE_MEDIA_COLLECTION_ID);
  const adverseMediaEntityTypeConflict =
    adverseMediaSelected &&
    entityType.some((t) =>
      (ADVERSE_MEDIA_RESTRICTED_ENTITY_TYPES as readonly string[]).includes(t),
    );

  const canSearch =
    hasQuery && dataSources.length > 0 && !matchesActiveSearch && !adverseMediaEntityTypeConflict;

  const identifierError = showIdentifierError ? validateIdentifier(identifier) : null;
  const dobError = showDobError ? validateDob(dob) : null;
  // Adverse Media has no identifier-search capability at all — a name is
  // required whenever it's selected, even mixed in with other watchlists.
  const adverseMediaNameError =
    showAdverseMediaNameError && adverseMediaSelected && trimmedDraft.length === 0;

  return (
    <>
      <div className="@container flex w-full flex-col gap-2 rounded-sm border border-grey-200 bg-grey-50 p-2">
        {historical ? (
          <HistoricalSearchBar query={initialQuery} runAt={runAt!} />
        ) : (
          <>
            <SearchRow
              draft={draft}
              setDraft={setDraft}
              nameError={adverseMediaNameError}
              identifier={identifier}
              setIdentifier={setIdentifier}
              identifierError={identifierError}
              identifierDisabled={adverseMediaOnly}
              onSubmit={handleSubmit}
              canSearch={canSearch}
              searchDisabledReason={
                !hasQuery
                  ? "empty"
                  : adverseMediaEntityTypeConflict
                    ? "adverseMediaEntityType"
                    : matchesActiveSearch
                      ? "unchanged"
                      : undefined
              }
              onSaveDefault={handleSaveDefault}
              savingDefault={savingDefault}
              onRestoreDefault={handleRestoreDefault}
              hasSavedConfig={Boolean(userSanctionsConfigId)}
              restoringDefault={restoringDefault}
            />
            {adverseMediaNameError && <AdverseMediaNameRequiredHint />}
            {identifierError && (
              <p role="alert" className="px-1 text-tiny font-medium text-red-600">
                {identifierError}
              </p>
            )}
            {saveDefaultError && (
              <Alert tone="danger" role="alert" title="Couldn't save your config">
                {saveDefaultError}
              </Alert>
            )}
            {restoreDefaultError && (
              <Alert tone="danger" role="alert" title="Couldn't restore your config">
                {restoreDefaultError}
              </Alert>
            )}
          </>
        )}

        {isResults && !historical && (
          <button
            type="button"
            onClick={() => setShowFiltersOnNarrow((v) => !v)}
            aria-expanded={showFiltersOnNarrow}
            aria-controls="screening-filter-grid"
            className="flex h-11 w-full shrink-0 items-center justify-center gap-1.5 rounded-sm border border-grey-200 bg-white px-3 text-small font-medium text-grey-900 shadow-flat hover:bg-grey-50 @3xl:hidden"
          >
            <Icon name="tune" size={20} />
            <span>{showFiltersOnNarrow ? "Hide filters" : "Show filters"}</span>
            <Icon
              name={showFiltersOnNarrow ? "expand_less" : "expand_more"}
              size={20}
              className="ml-1 text-grey-500"
            />
          </button>
        )}

        <div
          id="screening-filter-grid"
          className={[
            "grid w-full grid-cols-1 gap-1 @xl:grid-cols-2 @3xl:grid-cols-3",
            isResults && !showFiltersOnNarrow && !historical ? "hidden @3xl:grid!" : "",
            historical ? "pointer-events-none opacity-60 select-none" : "",
          ].join(" ")}
        >
          <DataSourceCard
            selected={dataSources}
            onChange={(v) => {
              userTouchedRef.current = true;
              setDataSources(v);
            }}
            items={dataSourceItems}
            label="Watchlist"
            className="min-h-41 w-full @xl:col-span-2 @3xl:col-span-1"
            defaultSelected={EMPTY_FILTERS.watchlists}
            preventEmpty
            pepAllowed={pepFeatureEnabled}
            adverseMediaEnabled={adverseMediaFeatureEnabled}
            chipDescriptions={{
              [ADVERSE_MEDIA_COLLECTION_ID]: buildAdverseMediaTooltip(companyAdverseMedia),
            }}
          />

          <div className="flex w-full min-w-0 flex-col gap-1">
            <ConfidenceCard
              value={confidence}
              onChange={(v) => {
                userTouchedRef.current = true;
                setConfidence(v);
              }}
              className="w-full flex-1 justify-center"
            />
            <EntityTypeCard
              selected={entityType}
              onChange={(v) => {
                userTouchedRef.current = true;
                setEntityType(v);
              }}
              defaultSelected={EMPTY_FILTERS.entityType}
              className="w-full flex-1 justify-center"
            />
          </div>

          <div className="flex w-full min-w-0 flex-col gap-1">
            <CountryCard
              selected={countries}
              onChange={(v) => {
                userTouchedRef.current = true;
                setCountries(v);
              }}
            />
            <Tooltip
              title={adverseMediaOnly ? ADVERSE_MEDIA_UNUSED_FIELD_TOOLTIP : undefined}
              wrapperClassName="w-full flex-1"
            >
              <DobCard
                value={dob}
                onChange={setDob}
                error={dobError ?? undefined}
                className="w-full flex-1 justify-center"
                disabled={adverseMediaOnly}
              />
            </Tooltip>
          </div>
        </div>
      </div>
      {adverseMediaEntityTypeConflict && <AdverseMediaEntityTypeHint className="mt-1" />}
      {companyConfigLoaded && adverseMediaSelected && adverseMediaFeatureEnabled && (
        <AdverseMediaSettingsHint configured={hasCompanyAdverseMediaSettings} className="mt-1" />
      )}
    </>
  );
}

function SearchRow({
  draft,
  setDraft,
  nameError,
  identifier,
  setIdentifier,
  identifierError,
  identifierDisabled,
  onSubmit,
  canSearch = true,
  searchDisabledReason,
  onSaveDefault,
  savingDefault,
  onRestoreDefault,
  hasSavedConfig,
  restoringDefault,
}: {
  draft: string;
  setDraft: (v: string) => void;
  nameError?: boolean;
  identifier: string;
  setIdentifier: (v: string) => void;
  identifierError?: string | null;
  identifierDisabled?: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  canSearch?: boolean;
  /** Why the Search button is disabled — drives its tooltip. */
  searchDisabledReason?: "empty" | "unchanged" | "adverseMediaEntityType";
  onSaveDefault?: () => void;
  savingDefault?: boolean;
  onRestoreDefault?: () => void;
  hasSavedConfig?: boolean;
  restoringDefault?: boolean;
}) {
  return (
    <form role="search" onSubmit={onSubmit} className="flex w-full items-center gap-2">
      <NameInput value={draft} onChange={setDraft} error={nameError} />
      <Tooltip
        title={identifierDisabled ? ADVERSE_MEDIA_UNUSED_FIELD_TOOLTIP : undefined}
        wrapperClassName="flex-1 min-w-0"
      >
        <IdentifierInput
          value={identifier}
          onChange={setIdentifier}
          error={identifierError}
          disabled={identifierDisabled}
        />
      </Tooltip>
      {onSaveDefault && onRestoreDefault && (
        <SearchConfigMenu
          onSaveDefault={onSaveDefault}
          savingDefault={savingDefault}
          onRestoreDefault={onRestoreDefault}
          hasSavedConfig={hasSavedConfig}
          restoringDefault={restoringDefault}
        />
      )}
      <SearchButton canSearch={canSearch} disabledReason={searchDisabledReason} />
    </form>
  );
}

/** Bordered box wrapping a single borderless text input — its own filled/focus state. */
function InputBox({
  value,
  onChange,
  placeholder,
  ariaLabel,
  autoFocus,
  error,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  ariaLabel: string;
  autoFocus?: boolean;
  error?: string | boolean | null;
  disabled?: boolean;
}) {
  const filled = value.length > 0;
  return (
    <div
      className={[
        "flex h-11 min-w-0 flex-1 items-center rounded-sm border bg-white px-4 shadow-flat transition-colors",
        disabled ? "bg-grey-100" : "",
        error ? "border-red-500" : filled ? "border-grey-500" : "border-grey-200",
        "focus-within:border-grey-500",
      ].join(" ")}
    >
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        disabled={disabled}
        className="min-w-0 flex-1 border-0 bg-transparent text-body text-grey-900 outline-none placeholder:text-grey-600 disabled:cursor-not-allowed disabled:text-grey-500"
      />
    </div>
  );
}

/** Free-text name field — the primary search input. */
function NameInput({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: boolean;
}) {
  return (
    <InputBox
      value={value}
      onChange={onChange}
      placeholder="Search a name..."
      ariaLabel="Screening search"
      autoFocus
      error={error}
    />
  );
}

/** Free-text identifier field (passport, tax id, etc.) — backend `identifier`. */
function IdentifierInput({
  value,
  onChange,
  error,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
  disabled?: boolean;
}) {
  return (
    <InputBox
      value={value}
      onChange={onChange}
      placeholder="Identifier (passport / national ID)..."
      ariaLabel="Screening identifier"
      error={error}
      disabled={disabled}
    />
  );
}

/** Kebab menu bundling the saved search-configuration actions: Save and Restore. */
function SearchConfigMenu({
  onSaveDefault,
  savingDefault,
  onRestoreDefault,
  hasSavedConfig,
  restoringDefault,
}: {
  onSaveDefault: () => void;
  savingDefault?: boolean;
  onRestoreDefault: () => void;
  hasSavedConfig?: boolean;
  restoringDefault?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (event.target instanceof Node && wrapperRef.current.contains(event.target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative shrink-0">
      <Tooltip title="Manage search config" align="start" side="top" width="max-content">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Manage search config"
          style={{ cursor: "default" }}
          className={[
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border shadow-flat",
            open
              ? "border-grey-500 bg-grey-100 text-black"
              : "border-grey-200 bg-white text-grey-700 hover:bg-grey-50",
          ].join(" ")}
        >
          <Icon name="more_vert" size={24} />
        </button>
      </Tooltip>
      {open && (
        <div
          role="menu"
          aria-label="Manage config"
          className="absolute right-0 top-full z-20 mt-1 flex w-55 flex-col gap-0.5 rounded-sm border border-grey-200 bg-white p-1 shadow-flat"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSaveDefault();
            }}
            disabled={savingDefault}
            className="flex items-center gap-2 rounded-sm px-3 py-2 text-left text-small font-medium text-black transition-colors hover:bg-grey-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Icon name="save" size={20} className="text-black" />
            Save your config
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onRestoreDefault();
            }}
            disabled={!hasSavedConfig || restoringDefault}
            className="flex items-center gap-2 rounded-sm px-3 py-2 text-left text-small font-medium text-black transition-colors hover:bg-grey-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Icon name="restore" size={20} className="text-black" />
            Restore your config
          </button>
        </div>
      )}
    </div>
  );
}

const SEARCH_DISABLED_TOOLTIPS: Record<"empty" | "unchanged" | "adverseMediaEntityType", string> = {
  empty: "Enter a name or an identifier to search",
  unchanged: "Modify the query or a filter to search again",
  adverseMediaEntityType:
    "Adverse Media only supports the Individual and Organization entity types",
};

/** Submit button — disabled until the search is runnable. */
function SearchButton({
  canSearch,
  disabledReason,
}: {
  canSearch: boolean;
  disabledReason?: "empty" | "unchanged" | "adverseMediaEntityType";
}) {
  const button = (
    <button
      type="submit"
      disabled={!canSearch}
      aria-disabled={!canSearch}
      className="flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-sm border-b border-green-600 bg-green-500 px-4 py-2 text-body font-medium text-white shadow-flat-green hover:bg-green-400 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
    >
      <Icon name="search" size={20} />
      <span>Search</span>
    </button>
  );
  if (canSearch || !disabledReason) return button;
  return (
    <Tooltip title={SEARCH_DISABLED_TOOLTIPS[disabledReason]} align="end">
      {button}
    </Tooltip>
  );
}

function HistoricalSearchBar({ query, runAt }: { query: string; runAt: string }) {
  const formatted = formatRunAt(runAt);
  return (
    <div className="flex h-12 items-center gap-3 rounded-sm border border-grey-200 bg-white px-4 shadow-flat">
      <span className="flex-1 truncate text-body font-semibold text-black">{query}</span>
      <span className="text-small text-grey-600">Date of search:</span>
      <span className="text-small font-medium text-black">{formatted}</span>
    </div>
  );
}

function formatRunAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const h24 = d.getHours();
  const meridiem = h24 < 12 ? "a.m." : "p.m.";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}, ${h12}:${min}${meridiem}`;
}
